import { Title } from '@solidjs/meta'
import type { Component, JSX } from 'solid-js'
import { For } from 'solid-js'
import Badge from '~/components/Badge'
import Button from '~/components/Button'
import Card from '~/components/Card'
import Container from '~/components/Container'
import Icon from '~/components/Icon'
import Tooltip from '~/components/Tooltip'
import type { TooltipPlacementType } from '~/types/Tooltip'

const TooltipPage: Component = (): JSX.Element => {
  const placements: TooltipPlacementType[] = ['auto', 'top', 'bottom', 'left', 'right']

  const transitions: ('zoom' | 'fade' | 'grow')[] = ['zoom', 'fade', 'grow']

  return (
    <>
      <Title>Tooltip Component - Florez Jaramillo Solutions</Title>

      <Container class="py-8">
        <div class="mb-10 mt-6">
          <h1 class="text-4xl font-bold text-foreground mb-4">Tooltip</h1>
          <p class="text-foreground-subtle text-xl max-w-3xl leading-relaxed">
            Pequeñas ventanas emergentes informativas que aparecen cuando un usuario interactúa con un elemento o lo enfoca. Ideales para clarificar acciones o proporcionar contexto adicional sin
            saturar la interfaz.
          </p>
        </div>

        <div class="space-y-12">
          {/* Ejemplo Básico */}
          <Card>
            <Card.Header
              title="Uso Básico"
              subtitle="El tooltip se muestra al pasar el cursor o enfocar el elemento hijo."
              icon={<Icon icon="material-symbols-light:info-outline" class="text-primary-500" size={24} />}
            />
            <Card.Body>
              <div class="bg-surface-muted p-10 rounded-xl flex flex-wrap items-center justify-center gap-12 border border-border">
                <Tooltip content="Información importante">
                  <span class="text-lg font-medium text-foreground-muted border-b-2 border-dotted border-primary-500 cursor-help">Pasa el mouse sobre el texto</span>
                </Tooltip>

                <Tooltip content="Guardar cambios">
                  <Button color="primary" startIcon="material-symbols-light:save-outline">
                    Guardar
                  </Button>
                </Tooltip>

                <Tooltip content="Configuración del sistema">
                  <div class="p-3 bg-surface rounded-full shadow-md cursor-pointer hover:bg-slate-50 transition-colors">
                    <Icon icon="material-symbols-light:settings-outline" class="text-foreground-subtle" size={24} />
                  </div>
                </Tooltip>
              </div>
            </Card.Body>
          </Card>

          {/* Placements */}
          <Card>
            <Card.Header
              title="Posicionamiento (Placements)"
              subtitle="Controla hacia dónde emerge el tooltip respecto al elemento origen."
              icon={<Icon icon="material-symbols-light:fullscreen" class="text-secondary-500" size={24} />}
            />
            <Card.Body>
              <div class="flex flex-col gap-8">
                <p class="text-sm text-foreground-subtle italic">
                  Tip: El modo{' '}
                  <Badge color="primary" variant="soft">
                    auto
                  </Badge>{' '}
                  calculará el mejor lugar basado en el espacio disponible en el viewport.
                </p>
                <div class="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <For each={placements}>
                    {p => (
                      <div class="flex flex-col items-center gap-3 p-4 bg-surface-muted rounded-lg border border-border">
                        <Tooltip content={`Posición: ${p}`} placement={p}>
                          <Button color="secondary" variant="outlined" class="w-full">
                            {p}
                          </Button>
                        </Tooltip>
                      </div>
                    )}
                  </For>
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* Transitions */}
          <Card>
            <Card.Header
              title="Efectos de Transición"
              subtitle="Personaliza la animación de entrada y salida para que se ajuste al tono de tu app."
              icon={<Icon icon="material-symbols-light:play-circle-outline" class="text-amber-500" size={24} />}
            />
            <Card.Body>
              <div class="flex flex-wrap items-center justify-center gap-8 p-8 bg-surface-muted rounded-xl border border-border">
                <For each={transitions}>
                  {t => (
                    <div class="text-center">
                      <Tooltip content={`Efecto ${t}`} transition={t} duration={200}>
                        <Button variant="text" color="warning" class="capitalize text-lg">
                          {t}
                        </Button>
                      </Tooltip>
                    </div>
                  )}
                </For>

                <div class="h-10 w-px bg-surface-muted hidden md:block" />

                <Tooltip content="Duración lenta de 800ms" transition="zoom" duration={800}>
                  <Button color="warning" variant="outlined">
                    Animación Lenta
                  </Button>
                </Tooltip>
              </div>
            </Card.Body>
          </Card>

          {/* Contenido Complejo */}
          <Card>
            <Card.Header
              title="Contenido Rico (HTML/JSX)"
              subtitle="Los tooltips pueden contener elementos interactivos o formato avanzado."
              icon={<Icon icon="material-symbols-light:movie-outline" class="text-emerald-500" size={24} />}
            />
            <Card.Body>
              <div class="flex flex-wrap items-center justify-center gap-12 p-10 bg-surface-muted rounded-xl border border-border">
                <Tooltip
                  content={
                    <div class="flex flex-col gap-2 p-1">
                      <div class="flex items-center gap-2">
                        <Icon icon="material-symbols-light:star-outline" class="text-amber-400" size={14} />
                        <span class="font-bold">Usuario VIP</span>
                      </div>
                      <p class="text-[10px] opacity-80">Miembro desde Diciembre 2023</p>
                      <div class="h-px bg-white/20 my-1" />
                      <div class="flex gap-1">
                        <Badge color="success" size="sm">
                          Activo
                        </Badge>
                        <Badge color="info" size="sm">
                          Premium
                        </Badge>
                      </div>
                    </div>
                  }
                >
                  <div class="flex items-center gap-3 cursor-pointer p-2 rounded-lg hover:bg-surface-hover transition-all shadow-sm group">
                    <div class="w-10 h-10 rounded-full bg-success-soft flex items-center justify-center">
                      <Icon icon="material-symbols-light:person-outline" class="text-emerald-600" size={24} />
                    </div>
                    <span class="font-semibold text-foreground">Perfil de Usuario</span>
                  </div>
                </Tooltip>

                <Tooltip
                  content={
                    <div class="text-center p-1">
                      <p class="mb-2">¿Confirmar despacho?</p>
                      <div class="flex justify-center">
                        <span class="px-2 py-0.5 bg-white/20 rounded text-[10px]">Acción rápida</span>
                      </div>
                    </div>
                  }
                >
                  <Button color="info" startIcon="material-symbols-light:local-shipping-outline">
                    Enviar Pedido
                  </Button>
                </Tooltip>
              </div>
            </Card.Body>
          </Card>

          {/* Contenedor Scrollable */}
          <Card>
            <Card.Header
              title="Contextos de Desplazamiento"
              subtitle="El tooltip detecta padres con scroll y se reposiciona inteligentemente (Smart Portaling)."
              icon={<Icon icon="material-symbols-light:mouse-outline" class="text-blue-500" size={24} />}
            />
            <Card.Body>
              <div class="flex flex-col gap-4">
                <p class="text-sm text-foreground-subtle">Prueba haciendo scroll dentro de este recuadro para ver cómo el tooltip sigue al elemento o se oculta si no hay espacio.</p>
                <div class="border border-border rounded-xl h-64 overflow-auto bg-surface p-0 shadow-inner">
                  <div class="h-[600px] p-8 flex flex-col items-center gap-20">
                    <Tooltip content="Elemento al inicio" placement="right">
                      <div class="p-4 bg-info-soft text-on-info-soft rounded border border-info/25 font-medium"> Superior </div>
                    </Tooltip>

                    <div class="h-32 w-px bg-surface-muted" />

                    <Tooltip content="Elemento en el centro" placement="top">
                      <div class="p-4 bg-primary-soft text-on-primary-soft rounded border border-primary/25 font-medium"> Central </div>
                    </Tooltip>

                    <div class="h-32 w-px bg-surface-muted" />

                    <Tooltip content="Elemento al final" placement="left">
                      <div class="p-4 bg-success-soft text-on-success-soft rounded border border-success/25 font-medium"> Inferior </div>
                    </Tooltip>
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* API Reference */}
          <Card>
            <Card.Header
              title="API Reference"
              subtitle="Detalle de propiedades para configurar el comportamiento del Tooltip."
              icon={<Icon icon="material-symbols-light:code" class="text-foreground-subtle" size={24} />}
            />
            <Card.Body>
              <div class="overflow-x-auto rounded-xl border border-border">
                <table class="w-full text-sm text-left">
                  <thead class="text-xs uppercase bg-surface-muted text-foreground-subtle">
                    <tr>
                      <th class="px-6 py-4">Propiedad</th>
                      <th class="px-6 py-4">Tipo</th>
                      <th class="px-6 py-4">Default</th>
                      <th class="px-6 py-4">Descripción</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-border">
                    <tr>
                      <td class="px-6 py-4 font-mono text-primary font-bold">content</td>
                      <td class="px-6 py-4 text-foreground-subtle">string | JSX.Element</td>
                      <td class="px-6 py-4 font-bold">-</td>
                      <td class="px-6 py-4 text-foreground-muted">El mensaje o contenido que se mostrará dentro de la burbuja.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono text-primary font-bold">placement</td>
                      <td class="px-6 py-4 text-foreground-subtle">'top' | 'bottom' | 'left' | 'right' | 'none' | 'auto'</td>
                      <td class="px-6 py-4">'auto'</td>
                      <td class="px-6 py-4 text-foreground-muted">Ubicación preferida del tooltip. 'auto' calcula el mejor lugar.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono text-primary font-bold">transition</td>
                      <td class="px-6 py-4 text-foreground-subtle">'zoom' | 'fade' | 'grow'</td>
                      <td class="px-6 py-4">'zoom'</td>
                      <td class="px-6 py-4 text-foreground-muted">Tipo de animación aplicada al aparecer/desaparecer.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono text-primary font-bold">duration</td>
                      <td class="px-6 py-4 text-foreground-subtle">number</td>
                      <td class="px-6 py-4">160ms</td>
                      <td class="px-6 py-4 text-foreground-muted">Duración en milisegundos de la transición.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono text-primary font-bold">class</td>
                      <td class="px-6 py-4 text-foreground-subtle">string</td>
                      <td class="px-6 py-4">''</td>
                      <td class="px-6 py-4 text-foreground-muted">Clases personalizadas para el cuerpo del tooltip.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono text-primary font-bold">children</td>
                      <td class="px-6 py-4 text-foreground-subtle">JSX.Element</td>
                      <td class="px-6 py-4 font-bold">-</td>
                      <td class="px-6 py-4 text-foreground-muted">El elemento que disparará la aparición del tooltip.</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </Card.Body>
          </Card>
        </div>
      </Container>
    </>
  )
}

export default TooltipPage
